import { useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Download } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Carousel, CarouselItem, carouselStep } from '@worldcoo/ui/components/carousel';
import { annualReportUrl, annualReports } from '@/lib/annual-reports';
import { useTranslation } from '@/lib/i18n';
import { DiamondGrid, diamondGlows } from '../primitives';

/**
 * The reports are the ones the transparency page already links to — `src/lib/annual-reports.ts` is their
 * single home, so a new memoria is added once and both pages pick it up.
 *
 * REVERSED here because that list is oldest-first and this rail runs newest at the top; the copy keeps
 * the shared data from having either page's ordering baked into it.
 */
const REPORTS = [...annualReports].reverse();

/** The rail marker, an 8px brand disc with a 4px hole — the same figure the milestone rail uses. */
function YearMarker() {
	return (
		<span aria-hidden className="bg-brand relative block size-2 rounded-full">
			<span className="absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
		</span>
	);
}

/**
 * "Nuestro impacto, año a año" — the annual reports carousel.
 *
 * The copy sits left with a footnote beneath it, one sand card holds the selected year, and the rail on
 * the right is VERTICAL here (newest at the top) rather than horizontal like the milestones one.
 *
 * The card is the shared `Carousel`, a native scroll-snap track: one slide is exactly the frame's
 * width, so at rest exactly one year shows — as drawn — while a swipe brings the next one in, with the
 * momentum, the snapping and the keyboard all coming from the browser rather than from a drag gesture
 * we animate ourselves. It scrolls horizontally even though the rail runs vertically: a vertical track
 * would have to claim the page's own scroll gesture on a phone.
 *
 * WHICH MAKES THE SCROLL POSITION THE STATE. `active` is read back off it rather than set alongside it,
 * so the rail says what is actually on screen however the year got there — and the rail's own buttons
 * scroll the track and let that read-back follow, rather than moving an index the next scroll event
 * would overwrite.
 */
export function Reports() {
	const { t } = useLingui();
	const { language } = useTranslation();
	// Opens on the newest report, which is index 0 because `REPORTS` is reversed. It used to open two
	// down, on 2023, to match the hand-off — but the hand-off draws 2023 because 2023 was the newest
	// when it was drawn, and a fixed index does not know that. Left alone it walks backwards a year
	// every time `annual-reports.ts` gains an entry.
	const [active, setActive] = useState(0);
	const track = useRef<HTMLUListElement>(null);
	const last = REPORTS.length - 1;

	/**
	 * The rail's jump. `behavior` is left unset on purpose: `scrollTo` then defers to the track's own
	 * `scroll-behavior`, which the carousel already makes smooth only when motion is not being reduced.
	 */
	const showYear = (index: number) => {
		const el = track.current;
		if (el) el.scrollTo({ left: index * carouselStep(el) });
	};

	const onScroll = () => {
		const el = track.current;
		if (!el) return;
		const step = carouselStep(el);
		if (step > 0) setActive(Math.min(last, Math.max(0, Math.round(el.scrollLeft / step))));
	};

	return (
		<section className="relative overflow-hidden bg-white">
			{/* A single brand glow running off the bottom-left corner: the export puts a 777x501 ellipse at
			    left -398 with its top 352.5px below the centre line, which centres it at (-9.5, 726.5). */}
			<DiamondGrid
				color="var(--color-brand)"
				opacity={0.95}
				masks={[diamondGlows({ x: '-10px', y: 'calc(100% - 22px)' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-10 px-6 py-16 md:py-20 xl:h-[748px] xl:gap-0 xl:px-0 xl:py-0">
				<div className="flex w-full max-w-[513px] flex-col gap-4 xl:absolute xl:top-[183px] xl:left-[93px]">
					<h2 className="text-ink text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`Our impact, year by year`}
					</h2>
					<p className="text-ink max-w-[496px] text-lg leading-[1.5] tracking-[-0.02em] md:text-xl">
						{t`We invite you to consult our annual corporate reports for the detail of the social impact achieved.`}
					</p>
				</div>

				{/* The card and the rail travel together on mobile, so they stay one control. */}
				<div className="flex items-center gap-4 md:gap-8 xl:contents">
					{/* 529x383 at left 707. The track scrolls INSIDE this box, which is what keeps a single year on
					    screen at rest while it holds all seven. The carousel's own floating scroll buttons are
					    off: the year rail beside it is this carousel's control, and the hand-off draws no arrows.
					    `gap-0`/`px-0` undo the shared defaults — a slide has to be the frame's exact width for
					    one year to fill it and for the snap points to land on the card edges. */}
					<Carousel
						ref={track}
						onScroll={onScroll}
						className="bg-sand carousel-no-buttons relative aspect-[529/383] min-w-0 flex-1 gap-0 rounded-3xl px-0 select-none xl:absolute xl:top-[183px] xl:left-[707px] xl:aspect-auto xl:h-[383px] xl:w-[529px] xl:flex-none"
					>
						{REPORTS.map((report, i) => (
							<CarouselItem
								key={report.year}
								// `overflow-hidden` on the SLIDE, not just the frame: the illustration overhangs the
								// card's right edge by design, and without this it spills into the neighbouring slide
								// instead of being cropped — which showed up as the previous year's drawing leaking
								// into the left of the visible card.
								className="relative h-full w-full overflow-hidden"
								aria-hidden={i !== active}
							>
								{/* 416x373 at (186, 36) of the card, which overflows its right edge on purpose.
								    PROVISIONAL: the hand-off has ONE drawing, 2023's, and it stands in for all seven years
								    until the rest arrive, so the rail repeats it. `object-contain` keeps it undistorted
								    inside a slot whose aspect is not its own.

								    Decorative, hence no alt and no msgid: the year and the download link beside it already
								    name the card, and a msgid for scaffolding that gets replaced the moment the other six
								    drawings land would cost ten translations for nothing. */}
								<img
									src="/redesign/about/high-five.svg"
									alt=""
									aria-hidden
									className="pointer-events-none absolute top-[9.4%] left-[35.2%] h-[97.4%] w-[78.6%] object-contain"
								/>

								{/* 72px, right-aligned in a 175 box at (44, 33) of the card. #F86A00 is the export's
								    own value here and is a shade off `brand-strong` (#F66900), so it stays literal. */}
								<p className="absolute top-[8.6%] left-[8.3%] w-[33.1%] text-right text-5xl leading-[1.3] font-medium text-[#F86A00] tabular-nums md:text-[4.5rem]">
									{report.year}
								</p>

								{/* (48, 316) of the card. No click guard needed any more: a native scroll is not a click,
								    so a swipe that ends over the link no longer opens a PDF on its own. */}
								<a
									href={annualReportUrl(report.links, language)}
									target="_blank"
									rel="noopener noreferrer"
									tabIndex={i === active ? undefined : -1}
									className="text-ink absolute top-[82.5%] left-[9.1%] flex items-center gap-2 text-[0.9375rem] leading-[1.3] font-medium tracking-[-0.02em] transition-opacity hover:opacity-70"
								>
									{t`Download`}
									<Download className="size-4" />
								</a>
							</CarouselItem>
						))}
					</Carousel>

					{/* The rail: a two-column grid so the marker lands in the active year's own ROW without any
					    measuring, and the line is one more item spanning every row of the first column. */}
					<div
						className="grid shrink-0 gap-x-[6px] gap-y-2 xl:absolute xl:top-[266px] xl:left-[1302px]"
						style={{ gridTemplateColumns: 'auto auto', gridTemplateRows: `repeat(${REPORTS.length}, auto)` }}
					>
						<span
							aria-hidden
							style={{ gridColumn: 1, gridRow: '1 / -1' }}
							// Inset 8px top and bottom, as the export's 201px line is inside a 216px column. Its
							// colour is not in the export; 20% matches the inactive years it sits beside.
							className="bg-ink/20 my-2 w-px justify-self-center"
						/>
						<span
							aria-hidden
							style={{ gridColumn: 1, gridRow: active + 1 }}
							className="self-center justify-self-center"
						>
							<YearMarker />
						</span>
						{REPORTS.map((report, i) => (
							<button
								key={report.year}
								type="button"
								onClick={() => showYear(i)}
								aria-current={i === active ? 'true' : undefined}
								style={{ gridColumn: 2, gridRow: i + 1 }}
								className={cn(
									'text-ink cursor-pointer text-base leading-[1.5] font-medium tracking-[-0.02em] transition-opacity',
									// 20% when inactive, not the 50% the milestone rail uses — this rail is quieter.
									i === active ? 'opacity-100' : 'opacity-20 hover:opacity-50',
								)}
							>
								{report.year}
							</button>
						))}
					</div>
				</div>

				{/* 533 wide at (93, 544), 14px at 70%. */}
				<p className="text-ink/70 w-full max-w-[533px] text-sm leading-[1.5] tracking-[-0.02em] xl:absolute xl:top-[544px] xl:left-[93px]">
					{t`*Financial reports audited annually by AUDRIA Auditoría y Consultoría, S.L.P.`}
				</p>
			</div>
		</section>
	);
}
